import React, { useState, useEffect, useCallback } from 'react';
import { Form, Input, Button, Card, message, Typography, Spin, InputNumber, Upload } from 'antd';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useNavigate, useParams } from 'react-router-dom';
import { measureNewsApi, uploadApi } from '../../services/api';

const { Title } = Typography;

const MeasureNewsForm = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [coverUrl, setCoverUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const fetchNewsDetail = useCallback(async () => {
    setLoading(true);
    try {
      const response = await measureNewsApi.getById(id);
      const data = response.data;
      const decodedTitle = data.title ? decodeURIComponent(data.title) : '';
      const decodedContent = data.content ? decodeURIComponent(data.content) : '';
      const coverImageUrl = data.cover;

      form.setFieldsValue({
        title: decodedTitle,
        cover: coverImageUrl,
        content: decodedContent,
        sort: data.sort,
      });

      if (coverImageUrl) {
        setCoverUrl(coverImageUrl);
        setFileList([
          {
            uid: '-1',
            name: 'image.png',
            status: 'done',
            url: coverImageUrl,
          },
        ]);
      }
    } catch (error) {
      message.error('Measureニュース詳細の取得に失敗しました：' + (error.response?.data?.message || 'ネットワークエラー'));
    } finally {
      setLoading(false);
    }
  }, [id, form]);

  useEffect(() => {
    if (isEdit) {
      fetchNewsDetail();
    }
  }, [isEdit, fetchNewsDetail]);

  const onFinish = async (values) => {
    setSubmitting(true);
    try {
      values.cover = coverUrl || '';
      if (isEdit) {
        await measureNewsApi.update(id, values);
        message.success('更新しました');
      } else {
        await measureNewsApi.create(values);
        message.success('作成しました');
      }
      navigate('/measure-news');
    } catch (error) {
      message.error((isEdit ? '更新' : '作成') + 'に失敗しました：' + (error.response?.data?.message || 'ネットワークエラー'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpload = async ({ file, onSuccess, onError }) => {
    setUploading(true);
    try {
      const response = await uploadApi.uploadImage(file);
      const url = response.data;
      setCoverUrl(url);
      form.setFieldsValue({ cover: url });
      setFileList([
        {
          uid: file.uid || '-1',
          name: file.name || 'image.png',
          status: 'done',
          url,
        },
      ]);
      onSuccess?.();
      message.success('画像アップロード成功');
    } catch (error) {
      setFileList([
        {
          uid: file.uid || '-1',
          name: file.name || 'image.png',
          status: 'error',
        },
      ]);
      onError?.(error);
      message.error('画像アップロード失敗：' + (error.response?.data?.message || 'ネットワークエラー'));
    } finally {
      setUploading(false);
    }
  };

  const handleChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
    if (newFileList.length === 0) {
      setCoverUrl('');
      form.setFieldsValue({ cover: '' });
    }
  };

  const handlePreview = async (file) => {
    let src = file.url;
    if (!src) {
      src = await new Promise(resolve => {
        const reader = new FileReader();
        reader.readAsDataURL(file.originFileObj);
        reader.onload = () => resolve(reader.result);
      });
    }
    const image = new Image();
    image.src = src;
    const imgWindow = window.open(src);
    imgWindow?.document.write(image.outerHTML);
  };

  return (
    <Card>
      <Title level={4}>{isEdit ? 'Measureニュース編集' : 'Measureニュース作成'}</Title>
      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          autoComplete="off"
        >
          <Form.Item
            label="タイトル"
            name="title"
            rules={[{ required: true, message: 'タイトルを入力してください!' }]}
          >
            <Input placeholder="Measureニュースのタイトルを入力してください" />
          </Form.Item>

          <Form.Item
            label="カバー画像"
            name="cover"
          >
            <Upload
              customRequest={handleUpload}
              listType="picture-card"
              fileList={fileList}
              onChange={handleChange}
              onPreview={handlePreview}
              maxCount={1}
              disabled={uploading}
            >
              {fileList.length < 1 && '+ Upload'}
            </Upload>
          </Form.Item>

          <Form.Item
            label="ソート"
            name="sort"
            initialValue={0}
          >
            <InputNumber min={0} style={{ width: '100%' }} placeholder="ソート値を入力してください" />
          </Form.Item>

          <Form.Item
            label="内容"
            name="content"
            rules={[{ required: true, message: '内容を入力してください!' }]}
          >
            <ReactQuill
              modules={{
                toolbar: [
                  [{ 'header': [1, 2, false] }],
                  ['bold', 'italic', 'underline', 'strike'],
                  [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                  ['link', 'image'],
                  ['clean']
                ]
              }}
              style={{ background: 'white' }}
              placeholder="Measureニュースの内容を入力してください"
            />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {isEdit ? '更新' : '作成'}
            </Button>
            <Button
              style={{ marginLeft: 8 }}
              onClick={() => navigate('/measure-news')}
            >
              キャンセル
            </Button>
          </Form.Item>
        </Form>
      </Spin>
    </Card>
  );
};

export default MeasureNewsForm;
